import React, { useState } from "react";
import { Form, Button, Col } from "react-bootstrap";
import { submitInstitution } from "../../../api/services/masterService";
import { StyledFormGroup, Feedback } from "./Common";

const InstitutionField = ({
    register,
    setValue, // for specific value updates
    sectionErrors,
    detailsPath,
    fieldName = "collegeName",
    label = "Organization Name",
    institutions = [],
    onInstitutionAdded, // function (newInst) => void
    selectedState,
    selectedDistrict,
    level,
}) => {
    const [isOtherSelected, setIsOtherSelected] = useState(false);
    const [manualInstitution, setManualInstitution] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    // If district isn't selected, field is disabled
    const isDisabled = !selectedDistrict;

    const handleSaveOther = async () => {
        if (!manualInstitution) return alert("Please enter institution name");

        setIsSaving(true);
        try {
            const payload = {
                name: manualInstitution,
                stateCode: selectedState,
                districtName: selectedDistrict,
                level: level,
                type: "Private",
            };

            await submitInstitution(payload);

            const newInst = { name: manualInstitution, _id: `temp-${Date.now()}` };

            // Update parent list
            if (onInstitutionAdded) {
                onInstitutionAdded(newInst);
            }

            // Update form value
            setValue(`${detailsPath}.${fieldName}`, manualInstitution, { shouldValidate: true });

            // Reset local state
            setIsOtherSelected(false);
            setManualInstitution("");

            alert("Institution submitted for approval! You can now continue.");
        } catch (error) {
            alert(error.response?.data?.message || "Failed to save institution");
        } finally {
            setIsSaving(false);
        }
    };

    const handleSelectChange = (e) => {
        const value = e.target.value;
        if (value === "OTHER") {
            setIsOtherSelected(true);
            setValue(`${detailsPath}.${fieldName}`, ""); // clear hidden value
        } else {
            // Use standard react-hook-form behavior
            // But we need to update the value manually if we bypassed register's onChange handling?
            // Actually, passing onChange to register prop overrides internal behavior, so we must be careful.
            // The register return contains { onChange, onBlur, name, ref }.
            // We can create a wrapped onChange.
            // Or just use setValue entirely.
            // Let's rely on setValue to be safe as we are customizing the dropdown heavily.
            setValue(`${detailsPath}.${fieldName}`, value, { shouldValidate: true });
        }
    };

    // We need to register the field, but maybe handle onChange manually for the "Other" logic
    // "register" props spread might interfere. Let's extract errors and use Controller pattern manually or just standard.
    // Standard approach:
    const registration = register(`${detailsPath}.${fieldName}`);

    return (
        <StyledFormGroup as={Col} md="4">
            <Form.Label>
                {label} <span className="text-danger">*</span>
            </Form.Label>

            {!isOtherSelected ? (
                <Form.Select
                    {...registration}
                    disabled={isDisabled}
                    isInvalid={!!sectionErrors[fieldName]}
                    onChange={(e) => {
                        registration.onChange(e); // call RHF handler
                        handleSelectChange(e);    // call our handler
                    }}
                >
                    <option value="">
                        {selectedDistrict ? "Select Institution" : "Select District first"}
                    </option>
                    {institutions.length === 0 && selectedDistrict && (
                        <option value="" disabled>
                            No institutions found
                        </option>
                    )}
                    {institutions.map((inst) => (
                        <option key={inst._id || inst.name} value={inst.name}>
                            {inst.name}
                        </option>
                    ))}
                    {selectedDistrict && (
                        <option
                            value="OTHER"
                            style={{ fontWeight: "bold", color: "#4b6cb7" }}
                        >
                            + Other (Not in list? Add here)
                        </option>
                    )}
                </Form.Select>
            ) : (
                <div className="d-flex gap-2">
                    <Form.Control
                        type="text"
                        placeholder="Enter Institution Name"
                        value={manualInstitution}
                        onChange={(e) => setManualInstitution(e.target.value)}
                    />
                    <Button
                        variant="success"
                        onClick={handleSaveOther}
                        disabled={isSaving}
                    >
                        {isSaving ? "..." : "Save"}
                    </Button>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setIsOtherSelected(false)}
                    >
                        ✕
                    </Button>
                </div>
            )}
            <Feedback error={sectionErrors[fieldName]} />
        </StyledFormGroup>
    );
};

export default InstitutionField;
